/**
 * AnexosBlock — anexos do lead no cockpit.
 *
 * Desde o CRM-50 a lista é o `DocumentoGrid` (mesmo desenho e mesmo
 * visualizador da aba Documentos do Cliente 360, como o dono pediu) e o
 * arquivo abre pelo proxy autenticado `download_url` — antes era um link para
 * a URL assinada do S3, que expira em 1 h com a aba aberta e, quando o arquivo
 * sumiu do bucket, entregava a página XML NoSuchKey da AWS.
 *
 * Carrega a própria lista de `/api/crm/anexos?lead=` (em vez de ler
 * `lead.anexos` do detalhe) para refletir upload/exclusão sem recarregar o
 * cockpit inteiro.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPaperclip } from "react-icons/fi";

import AnexoUploadForm from "../../../components/AnexoUploadForm";
import Block from "../../../components/Block";
import DocumentoGrid from "../../../components/DocumentoGrid";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  excluirAnexoLead,
  listarAnexosLead,
  uploadAnexoLead,
} from "../../../services/crm";
import { anexoParaDocumento } from "../../../utils/documentos";
import { EmptyMsg } from "../../Orcamentos/styles";

function AnexosBlock({ leadId, podeEditar, collapsible, defaultCollapsed }) {
  const { request } = useAuth();
  const toast = useToast();
  const [anexos, setAnexos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const payload = await listarAnexosLead(request, leadId, { silentError: true });
      setAnexos(Array.isArray(payload) ? payload : payload?.results || []);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar anexos.");
    } finally {
      setCarregando(false);
    }
  }, [request, leadId]);

  useEffect(() => {
    if (leadId) carregar();
  }, [leadId, carregar]);

  const documentos = useMemo(
    () => anexos.map((anexo) => anexoParaDocumento(anexo)),
    [anexos],
  );

  const enviar = async ({ arquivo, descricao }) => {
    await uploadAnexoLead(request, { leadId, arquivo, descricao }, { silentError: true });
    await carregar();
  };

  const excluir = async (doc) => {
    await excluirAnexoLead(request, doc.id, { silentError: true });
    await carregar();
    // O upload avisa pelo AsyncButton; a exclusão não avisava nada, e o card
    // sumindo era a única pista de que o arquivo foi apagado (CRM-50).
    toast.success("Anexo excluído.");
  };

  return (
    <Block
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      title={
        <>
          <FiPaperclip
            size={13}
            style={{ verticalAlign: "middle", marginRight: 4 }}
            aria-hidden="true"
          />
          Anexos ({anexos.length})
        </>
      }
    >
      <DocumentoGrid
        documentos={documentos}
        carregando={carregando}
        erro={erro}
        onExcluir={podeEditar ? excluir : null}
        minColuna={200}
        vazio={<EmptyMsg style={{ padding: 8 }}>Nenhum anexo.</EmptyMsg>}
      />

      {podeEditar && <AnexoUploadForm onEnviar={enviar} />}
    </Block>
  );
}

export default AnexosBlock;
